import React, { useContext, useRef, useState } from 'react'
import { css } from '@emotion/core'
import styled from '@emotion/styled'
import { ParsedTrace } from '../../types/DataTypes'
import { Button, Icon, MessageBox, Table, Typography } from '@dw/helix'
import { ExportToCsv } from 'export-to-csv'

import { infoRowStyles, noSearchResultsStyles, numberOfResultsStyles, panelStyles, placeRightStyles } from './styles'
import { AuthContext, UserGroup } from '../../contexts'
import features from '../../config/feature-flags'

interface GroupedTrace {
  key: string
  productId: string
  traceTime: string
  traceEvent: string
  orderNo: string
  userLocation: string
  userName: string
  count: number
}

interface OrderResultsProps {
  traces: ParsedTrace[] | null
  error: boolean | undefined
  orderNumber: string
  canAccessOrderResults: boolean
  orderHasBeenDeleted: boolean
  toggleModal: (_: boolean) => void
}

const iconStyles = css`
  display: flex;
  width: 100%;
  max-width: 14px;
  max-height: 14px;
  margin-right: 12px;
  color: inherit;
`

const IconWrapper = styled.div`
  display: flex;
  align-items: flex-start;
`

export const OrderResults: React.FunctionComponent<OrderResultsProps> = props => {
  const [field, setField] = useState('productId')
  const { hasGroup } = useContext(AuthContext)
  const [ascending, setAscending] = useState(true)

  const textareaClipboard = useRef<HTMLTextAreaElement>(null)
  const columns = [
    {
      label: 'Article No.',
      field: 'productId',
      width: '136px',
      sortable: true,
    },
    {
      label: 'Trace Date',
      field: 'traceTime',
      width: '125px',
      sortable: true,
    },
    {
      label: 'Scanning Type',
      field: 'traceEvent',
      width: '127px',
      sortable: true,
    },
    {
      label: 'Order No.',
      field: 'orderNo',
      width: '136px',
      sortable: true,
    },
    {
      label: 'User Location',
      field: 'userLocation',
      width: '128px',
      sortable: true,
    },
    {
      label: 'Scanned By',
      field: 'userName',
      render: ({ userName }: any) => (
        <span
          title={userName}
          css={css`
            width: 90%;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          `}
        >
          {userName}
        </span>
      ),
      width: '108px',
      sortable: true,
    },
    {
      label: 'Count',
      field: 'count',
      width: '70px',
      sortable: true,
    },
  ]
  const traces = props.traces || []
  const hasTraces = props.traces !== null

  const groupedResults = Object.values(
    traces.reduce((acc, trace) => {
      const key = `${trace.productId}${trace.userLocation}${trace.traceEvent}`
      const { productId, traceTime, traceEvent, userLocation, userName, orderNo } = trace

      if (!acc[key]) {
        acc[key] = { key, productId, traceTime, traceEvent, orderNo, userLocation, userName, count: 1 }
        return acc
      }

      acc[key].count += 1

      if (acc[key].traceTime <= traceTime) {
        acc[key].traceTime = traceTime
        acc[key].traceEvent = traceEvent
        acc[key].userLocation = userLocation
        acc[key].userName = userName
      }
      return acc
    }, {} as Record<string, GroupedTrace>),
  )

  const sortedResults = groupedResults.sort((a, b) => {
    // @ts-ignore
    if (a[field] > b[field]) return ascending ? 1 : -1

    // @ts-ignore
    if (a[field] < b[field]) return ascending ? -1 : 1

    if (a.count > b.count) return ascending ? 1 : -1
    if (a.count < b.count) return ascending ? -1 : 1

    return 0
  })

  const copyToClipboard = () => {
    if (textareaClipboard.current) {
      textareaClipboard.current.select()
      document.execCommand('copy')
    }
  }

  const exportToCSV = () => {
    const csvExporter = new ExportToCsv({ filename: 'ITS-serials' })

    const dataForCsv = traces.map(
      ({
        orderNo,
        productId,
        traceEvent,
        traceTime,
        userId,
        userLocation,
        serialNo,
        shipTo,
        companySupplyChainRole,
        companyName,
      }) => ({
        serialNo,
        productId,
        orderNo,
        traceTime,
        traceEvent,
        companyName,
        userLocation,
        userId,
        companySupplyChainRole,
        shipTo,
      }),
    )

    csvExporter.generateCsv(dataForCsv)
  }

  const onSortClick = (newField: string, newAscending: boolean) => {
    setField(newField)
    setAscending(newAscending)
  }

  const hasDeleteAccess = hasGroup([UserGroup.Admin, UserGroup.SuperAdmin])
  return (
    <section>
      <div css={numberOfResultsStyles}>
        <Typography type="h4">{traces.length} results</Typography>
      </div>
      <div css={panelStyles}>
        <div css={infoRowStyles}>
          <Typography type="h4">{props.orderNumber && `Order no ${props.orderNumber}`}</Typography>
          {props.canAccessOrderResults && (
            <>
              <Button
                theme="tertiary"
                onClick={copyToClipboard}
                disabled={!hasTraces || props.orderHasBeenDeleted}
                css={placeRightStyles}
              >
                <IconWrapper>
                  <Icon name="plus" css={iconStyles} />
                  <span>Copy</span>
                </IconWrapper>
              </Button>
              <Button theme="tertiary" onClick={exportToCSV} disabled={!hasTraces || props.orderHasBeenDeleted}>
                <IconWrapper>
                  <Icon name="download" css={iconStyles} />
                  <span>Export</span>
                </IconWrapper>
              </Button>
            </>
          )}
          {hasDeleteAccess && features.delete_order && (
            <Button
              theme="tertiary"
              onClick={() => props.toggleModal(true)}
              disabled={!hasTraces || props.orderHasBeenDeleted}
            >
              <IconWrapper>
                <Icon name="trash" css={iconStyles} />
                <span>Delete</span>
              </IconWrapper>
            </Button>
          )}
        </div>
        <Table
          sortable
          items={sortedResults}
          columns={columns}
          rowKey="key"
          initialSortingField="productId"
          onSortClick={onSortClick}
        />
        {!props.error && !hasTraces && (
          <Typography css={noSearchResultsStyles} type="caption">
            No search results yet.
          </Typography>
        )}
        {props.error && (
          <MessageBox
            css={css`
              width: 100%;
              margin-top: 12px;
            `}
            type="error"
            data-testid="something-went-wrong-message"
          >
            Something went wrong. If the issue persists, please contact your admin.
          </MessageBox>
        )}
        {hasTraces && traces.length === 0 && !props.orderHasBeenDeleted && (
          <MessageBox
            css={css`
              width: 100%;
              margin-top: 12px;
            `}
            type="primary"
            className="e2e-test-no-results-message"
            data-testid="no-results-message"
          >
            No results found.
          </MessageBox>
        )}
        {props.orderHasBeenDeleted && (
          <MessageBox
            css={css`
              width: 100%;
              margin-top: 12px;
            `}
            type="primary"
            data-testid="deleted-order-message"
          >
            Order has been deleted.
          </MessageBox>
        )}
        <textarea
          ref={textareaClipboard}
          value={traces.map(trace => trace.serialNo).join('\n')}
          readOnly={true}
          // Hiding not styling
          style={{ position: 'absolute', left: '-9999px' }}
        />
      </div>
    </section>
  )
}
